import { createSlice, createAsyncThunk, PayloadAction, createAction } from '@reduxjs/toolkit';
import { setPreviewData } from './PreviewSlice';
import { useMemo, useRef, useEffect } from 'react';

// API 호출 추적을 위한 전역 변수
// 전역 변수는 JavaScript 모듈 스코프에 있어 앱 전체에서 공유됨
const pendingApiCalls: Record<string, Promise<any>> = {};

// API 우선순위 설정 - 숫자가 낮을수록 높은 우선순위
const API_PRIORITIES = {
  'fetchWithcookieData': 1,  // check_current가 최우선
  'checkAdminToken': 2,      // whoami 다음 우선순위
  'fetchPopupPosts': 3,      // 팝업 데이터는 세 번째
  'fetchTranslationDictionary': 4, // 다국어 사전은 네 번째
  'default': 10              // 기타 API 호출은 낮은 우선순위
};

// 현재 처리 중인 API 요청의 우선순위
let currentProcessingPriority = 100;

// 디바운싱 타이머 설정
let authCheckDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let productsDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let withcookieDataDebounceTimer: ReturnType<typeof setTimeout> | null = null;
const DEBOUNCE_TIME = 300; // 300ms

export const RESTRICTED_PATHS = ['/state-stop'];
// 결제 실패 정보 설정 액션 타입
const SET_PAYMENT_FAILURE = 'withcookie/setPaymentFailure';
// 결제 실패 정보 초기화 액션 타입
const CLEAR_PAYMENT_FAILURE = 'withcookie/clearPaymentFailure';

// 게시판 목록 조회 액션
export const fetchBoards = createAsyncThunk(
  'withcookie/fetchBoards',
  async (_, { rejectWithValue }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(
        `${apiBaseUrl}/api/post_categories/boards`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.boards || [];
    } catch (error: any) {
      return rejectWithValue(error.message || '게시판 목록을 불러오는데 실패했습니다.');
    }
  }
);

// 게시글 목록 조회 액션
export const fetchPosts = createAsyncThunk(
  'withcookie/fetchPosts',
  async ({ categoryId, page = 1, perPage = 10 }: { categoryId: number; page?: number; perPage?: number }, { rejectWithValue }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(
        `${apiBaseUrl}/api/posts/board/${categoryId}?page=${page}&take=10`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        posts: data.posts || [],
        totalCount: data.totalCount || 0,
        page,
        perPage
      };
    } catch (error: any) {
      return rejectWithValue(error.message || '게시글 목록을 불러오는데 실패했습니다.');
    }
  }
);

// 게시글 상세 조회 액션 (댓글 포함)
export const fetchPostDetail = createAsyncThunk(
  'withcookie/fetchPostDetail',
  async (params: any, { rejectWithValue }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const postId = typeof params === 'object' ? params.postId : params;
      
      
      const response = await fetch(
        `${apiBaseUrl}/api/posts/${postId}/with-comments`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include'
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.post || null;
    } catch (error: any) {
      return rejectWithValue(error.message || '게시글 상세 정보를 불러오는데 실패했습니다.');
    }
  }
);

// 게시글 생성 액션
export const createPost = createAsyncThunk(
  'withcookie/createPost',
  async (postData: { category_id: number; title: string; content: string; edit_type: string }, { rejectWithValue }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      
      const response = await fetch(
        `${apiBaseUrl}/api/posts`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(postData),
          credentials: 'include'
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error('[API] 게시글 생성 오류:', error);
      return rejectWithValue(error.message || '게시글 생성에 실패했습니다.');
    }
  }
);

// 게시글 수정 액션
export const updatePost = createAsyncThunk(
  'withcookie/updatePost',
  async ({ postId, postData }: { postId: number; postData: { title: string; content: string } }, { rejectWithValue }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(
        `${apiBaseUrl}/api/posts/${postId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify(postData)
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || '게시글 수정에 실패했습니다.');
    }
  }
);

// 게시글 삭제 액션
export const deletePost = createAsyncThunk(
  'withcookie/deletePost',
  async (postId: number, { rejectWithValue }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(
        `${apiBaseUrl}/api/posts/${postId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return postId;
    } catch (error: any) {
      return rejectWithValue(error.message || '게시글 삭제에 실패했습니다.');
    }
  }
);

// 댓글 작성 액션
export const createComment = createAsyncThunk(
  'withcookie/createComment',
  async ({ postId, commentData }: { postId: number; commentData: { content: string; edit_type: string } }, { rejectWithValue }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(
        `${apiBaseUrl}/api/posts/${postId}/comments`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify(commentData)
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || '댓글 작성에 실패했습니다.');
    }
  }
);

interface WithcookieState {
  withcookieData: any;
  savedProjects: any;
  loading: boolean;
  error: string | null;
  loginSuccess: boolean;
  loginError: string | null;
  validationErrors: any;
  signUpSuccess: boolean;
  signUpError: string | null;
  products: any[];
  productsLoading: boolean;
  productsError: string | null;
  productDetail: any;
  productDetailLoading: boolean;
  productDetailError: string | null;
  adminUser: any | null;
  isAdminLoggedIn: boolean;
  user: any | null;
  isUserLoggedIn: boolean;
  isLoggedIn: boolean;
  cartItems: any[];
  cartLoading: boolean;
  cartError: string | null;
  userInfo: any;
  userInfoLoading: boolean;
  userInfoError: string | null;
  companyIsUse: boolean; // 회사 사용 상태 추가
  addressResults: any[];
  addressLoading: boolean;
  addressError: string | null;
  selectedAddress: any | null;
  withcookieDataLastLoaded: number | null;
  // 메뉴 관련 상태 추가
  globalMenus: any[];
  mainMenus: any[];
  menuLoading: boolean;
  menuError: string | null;
  // 주문 내역 관련 상태 추가
  checkouts: any[];
  checkoutsLoading: boolean;
  checkoutsError: string | null;
  checkoutsPagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
  };
  // 결제 확인 관련 상태 추가
  paymentConfirmation: {
    loading: boolean;
    error: string | null;
    data: any | null;
    success: boolean;
  };
  // 결제 실패 관련 상태 추가
  paymentFailure: {
    errorCode: string | null;
    errorMessage: string | null;
    orderId: string | null;
  };
  // 바이너리 트리 관련 상태 추가
  binaryTreeData: any | null;
  binaryTreeLoading: boolean;
  binaryTreeError: string | null;
  // 팝업 관련 상태 추가
  popups: any[];
  popupsLoading: boolean;
  popupsError: string | null;
  // 게시판 관련 상태 추가
  boards: any[];
  boardsLoading: boolean;
  boardsError: string | null;
  posts: any[];
  postsLoading: boolean;
  postsError: string | null;
  postDetail: any | null;
  postDetailLoading: boolean;
  postDetailError: string | null;
  postsPagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
  };
  // 다국어 사전 관련 상태 추가
  translationDictionary: Record<string, Record<string, string>>;
  translationLoading: boolean;
  translationError: string | null;
  currentLanguage: string;
  // 리뷰 관련 상태 추가
  reviewLoading: boolean;
  reviewError: string | null;
  reviewSuccess: boolean;
  company: any | null; // company 객체 추가
}

const initialState: WithcookieState = {
  withcookieData: null,
  savedProjects: null,
  loading: false,
  error: null,
  loginSuccess: false,
  loginError: null,
  validationErrors: {},
  signUpSuccess: false,
  signUpError: null,
  products: [],
  productsLoading: false,
  productsError: null,
  productDetail: null,
  productDetailLoading: false,
  productDetailError: null,
  adminUser: null,
  isAdminLoggedIn: false,
  user: null,
  isUserLoggedIn: false,
  isLoggedIn: false,
  cartItems: [],
  cartLoading: false,
  cartError: null,
  userInfo: null,
  userInfoLoading: false,
  userInfoError: null,
  companyIsUse: true, // 기본값은 true로 설정
  addressResults: [],
  addressLoading: false,
  addressError: null,
  selectedAddress: null,
  withcookieDataLastLoaded: null,
  // 메뉴 관련 상태 초기화
  globalMenus: [],
  mainMenus: [],
  menuLoading: false,
  menuError: null,
  // 주문 내역 관련 상태 초기화
  checkouts: [],
  checkoutsLoading: false,
  checkoutsError: null,
  checkoutsPagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0
  },
  // 결제 확인 관련 상태 초기화
  paymentConfirmation: {
    loading: false,
    error: null,
    data: null,
    success: false
  },
  // 결제 실패 관련 상태 초기화
  paymentFailure: {
    errorCode: null,
    errorMessage: null,
    orderId: null
  },
  // 바이너리 트리 관련 상태 초기화
  binaryTreeData: null,
  binaryTreeLoading: false,
  binaryTreeError: null,
  // 팝업 관련 상태 초기화
  popups: [],
  popupsLoading: false,
  popupsError: null,
  // 게시판 관련 상태 초기화
  boards: [],
  boardsLoading: false,
  boardsError: null,
  posts: [],
  postsLoading: false,
  postsError: null,
  postDetail: null,
  postDetailLoading: false,
  postDetailError: null,
  postsPagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0
  },
  // 다국어 사전 관련 상태 초기화
  translationDictionary: {},
  translationLoading: false,
  translationError: null,
  currentLanguage: 'KR',
  // 리뷰 관련 상태 초기화
  reviewLoading: false,
  reviewError: null,
  reviewSuccess: false,
  company: null, // company 객체 초기화
};

interface MenuJsonData {
  previewData: string;
  previewPath: string;
  savedProjects: string;
  previewTimestamp: string;
  globalMenus?: string; // 글로벌 메뉴 데이터 추가
  mainMenus?: string;   // 주 메뉴 데이터 추가
}

// API 기본 URL 설정 함수
export const getApiBaseUrl = () => {
  const currentHost = window.location.hostname;
  
  
  // 운영 환경에서는 https 사용
  return `https://${currentHost}`;
};

// 관리자 토큰 확인 액션
export const checkAdminToken = createAsyncThunk(
  'withcookie/checkAdminToken',
  async ({ forceRefresh = false }: { forceRefresh?: boolean } = {}, { getState, dispatch, rejectWithValue }) => {
    try {
      // 호출 인스턴스 추적용 캐시 키
      const cacheKey = 'checkAdminToken';
      
      // 이미 상태가 있고 강제 갱신이 아니면 기존 상태 재사용
      const state = getState() as { withcookie: WithcookieState };
      if (!forceRefresh && state.withcookie.adminUser) {
        return { 
          isLoggedIn: state.withcookie.isAdminLoggedIn, 
          adminUser: state.withcookie.adminUser 
        };
      }

      // 진행 중인 호출이 있으면 재사용
      if (cacheKey in pendingApiCalls && !forceRefresh) {
        return pendingApiCalls[cacheKey];
      }

      // API 호출 생성 및 캐싱
      const apiBaseUrl = getApiBaseUrl();
      pendingApiCalls[cacheKey] = new Promise(async (resolve) => {
        try {
          const response = await fetch(
            `${apiBaseUrl}/api/administrators/whoami`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json'
              },
              credentials: 'include'
            }
          );

          if (!response.ok) {
            if (response.status >= 400) {
              dispatch(setAdminUser(null));
              resolve({ isLoggedIn: false, adminUser: null });
              return;
            }
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          
          // administrator 정보 사용
          if (data.administrator) {
            dispatch(setAdminUser(data.administrator));
          }
          
          resolve({ 
            isLoggedIn: !!data.administrator, 
            adminUser: data.administrator || null 
          });
        } catch (error) {
          console.error('어드민 인증 오류:', error);
          dispatch(setAdminUser(null));
          resolve({ isLoggedIn: false, adminUser: null });
        } finally {
          // 짧은 지연 후 캐시에서 제거
          setTimeout(() => {
            delete pendingApiCalls[cacheKey];
          }, 200);
        }
      });
      
      return pendingApiCalls[cacheKey];
    } catch (error: any) {
      return rejectWithValue(error.message || '관리자 인증 확인 중 오류가 발생했습니다.');
    }
  }
);

// 메뉴 JSON 저장 액션 생성
export const saveMenuJsonToServer = createAsyncThunk(
  'withcookie/saveMenuJson',
  async (data: MenuJsonData) => {
    try {
      const saving_data = {menuJson:data}
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(
        `${apiBaseUrl}/api/company-skins/menu-json-non-user`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(saving_data)
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw new Error(error.message || '서버 저장 중 오류가 발생했습니다.');
    }
  }
);

// 글로벌 및 주 메뉴 저장 액션 생성
export const saveMenus = createAsyncThunk(
  'withcookie/saveMenus',
  async (data: { globalMenus: any[], mainMenus: any[] }) => {

  }
);

// 글로벌 및 주 메뉴 불러오기 액션 생성
export const loadMenus = createAsyncThunk(
  'withcookie/loadMenus',
  async (_, { rejectWithValue }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(
        `${apiBaseUrl}/api/company-skins/menu-json-non-user`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      const menuJson = data.menuJson || {};
      
      // 메뉴 데이터 파싱
      let globalMenus = [];
      let mainMenus = [];
      
      if (menuJson.globalMenus) {
        try {
          globalMenus = JSON.parse(menuJson.globalMenus);
        } catch (e) {
          console.error('글로벌 메뉴 파싱 오류:', e);
        }
      }
      
      if (menuJson.mainMenus) {
        try {
          mainMenus = JSON.parse(menuJson.mainMenus);
        } catch (e) {
          console.error('주 메뉴 파싱 오류:', e);
        }
      }
      
      return { globalMenus, mainMenus };
    } catch (error: any) {
      return rejectWithValue(error.message || '메뉴 로드 중 오류가 발생했습니다.');
    }
  }
);

// 로그인 액션 생성
export const loginUser = createAsyncThunk(
  'withcookie/loginUser',
  async (userData: { user_id: string; password: string }, { rejectWithValue }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(
        `${apiBaseUrl}/api/users/login`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(userData)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue({
          message: errorData.message || `로그인에 실패했습니다. 상태: ${response.status}`,
          errors: errorData.errors
        });
      }

      const data = await response.json();
      
      // 로컬 스토리지에 토큰 저장 (필요시)
      if (data.token) {
        localStorage.setItem('auth_token', data.token);
      }

      return data;
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || '로그인 중 오류가 발생했습니다.',
        errors: {}
      });
    }
  }
);

// 회원가입 액션 생성
export const registerUser = createAsyncThunk(
  'withcookie/registerUser',
  async (userData: { user_id: string; password: string; phone: string; name: string; referral_code?: string }, { rejectWithValue }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      
      // FormData 객체 생성
      const formData = new URLSearchParams();
      
      // userData의 모든 키-값 쌍을 FormData에 추가
      Object.keys(userData).forEach(key => {
        if (userData[key as keyof typeof userData] !== undefined && userData[key as keyof typeof userData] !== null) {
          formData.append(key, String(userData[key as keyof typeof userData]));
        }
      });
      
      // API 요청 보내기
      const response = await fetch(
        `${apiBaseUrl}/api/users/register`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData.toString()
        }
      );

      
      
      // 응답 본문 가져오기
      const responseData = await response.json();

      if (!response.ok) {
        // 여러 에러 시나리오에 대해 사용자 친화적인 메시지 생성
        let errorMessage = responseData.message || `회원가입에 실패했습니다. 상태: ${response.status}`;
        
        // 403 에러 처리 (멤버십 자리 이미 존재)
        if (response.status === 403 && errorMessage.includes("해당 자리는 이미 회원이 존재합니다")) {
          errorMessage = "해당 위치에 이미 회원이 존재합니다. 다른 위치를 선택해주세요.";
        }
        
        // 409 에러 처리 (중복 아이디)
        if (response.status === 409) {
          errorMessage = "이미 사용 중인 아이디입니다. 다른 아이디를 입력해주세요.";
        }
        
        // 400 에러 처리 (유효성 검사 실패)
        if (response.status === 400) {
          errorMessage = "입력한 정보가 유효하지 않습니다. 정보를 확인 후 다시 시도해주세요.";
        }
        
        return rejectWithValue({
          message: errorMessage,
          errors: responseData.errors || {},
          statusCode: response.status
        });
      }

      return responseData;
    } catch (error: any) {
      console.error('[회원가입 예외]', error);
      return rejectWithValue({
        message: error.message || '회원가입 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
        errors: {},
        statusCode: 500
      });
    }
  }
);

// 상품 목록 가져오기 액션 생성
export const fetchProducts = createAsyncThunk<
  any[], // 반환 타입
  { // 첫 번째 매개변수 타입
    page?: number;
    per_page?: number;
    category_id?: number;
    include_product_ids?: number[];
    exclude_product_ids?: number[];
    include_category_ids?: number[];
    exclude_category_ids?: number[];
  }
>(
  'withcookie/products',
  async (
    options = {}, // 기본값 제공 
    { rejectWithValue, getState }
  ) => {
    try {
      // 디버깅: 요청 시간과 옵션 로깅
      const requestTime = new Date().toISOString();
      
      // 캐시 키 생성
      const cacheKey = `products_${JSON.stringify(options)}`;
      
      // 이미 캐시에 있는지 확인
      if (cacheKey in pendingApiCalls) {
        return pendingApiCalls[cacheKey];
      }
      
      // Redux 상태 확인
      const state = getState() as { withcookie: WithcookieState };
      const { products } = state.withcookie;
      
      // 현재 상태에 이미 제품이 있고, include_product_ids가 지정되었으며,
      // 해당 제품 ID가 이미 상태에 있는지 확인
      if (products && products.length > 0 && options.include_product_ids && options.include_product_ids.length > 0) {
        const existingProductIds = products.map(p => p.id);
        const requestedIds = options.include_product_ids;
        
        // 모든 요청된 상품 ID가 이미 상태에 있는지 확인
        const allProductsExist = requestedIds.every(id => existingProductIds.includes(id));
        
        // 이미 모든 제품이 있으면 상태를 재사용
        if (allProductsExist) {
          // 요청된 ID만 필터링하여 반환
          const filteredProducts = products.filter(p => requestedIds.includes(p.id));
          return filteredProducts;
        }
      }
      
      
      
      const apiBaseUrl = getApiBaseUrl();
      const apiEndpoint = `${apiBaseUrl}/api/products/list`;
      
      // API 호출 생성
      pendingApiCalls[cacheKey] = (async () => {
        try {
          // API 호출
          const requestOptions: RequestInit = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': '*/*',
              'Accept-Language': 'ko-KR,ko;q=0.9',
            },
            credentials: 'include',
            body: JSON.stringify(options)
          };
          
          const response = await fetch(apiEndpoint, requestOptions);
          
          
          if (!response.ok) {
            const errorMessage = `상품 목록을 불러오는데 실패했습니다. 상태: ${response.status}`;
            console.error(`[디버그 ${requestTime}] API 오류:`, errorMessage);
            throw new Error(errorMessage);
          }
          
          const data = await response.json();
          
          // API 응답 처리
          if (data && data.products && Array.isArray(data.products)) {
            const productCount = data.products.length;
            
            // 데이터 변환
            const formattedProducts = data.products.map((product: any) => {
              const defaultPrice = product.config?.default_price 
                ? Number(product.config.default_price) 
                : 0;
              const discountedPrice = product.config?.discounted_price 
                ? Number(product.config.discounted_price) 
                : defaultPrice;
              const hasDiscount = discountedPrice < defaultPrice;
              const stockCount = product.config?.stock_count ? Number(product.config.stock_count) : 0;
              
              return {
                id: product.id || 0,
                title: product.title || "상품명",
                description: product.description || "",
                newPrice: discountedPrice,
                oldPrice: defaultPrice,
                image: product.config?.img_url || null,
                imageTwo: product.config?.img_url ? product.config?.img_url : null,
                category: product.category_name || "기타",
                rating: 4, // API에서 제공하지 않음, 기본값 설정
                weight: product.config?.weight || "N/A",
                stockCount: stockCount,
                isInStock: stockCount > 0,
                sale: hasDiscount ? "Sale" : "",
                discountPercent: hasDiscount 
                  ? Math.round(100 - (discountedPrice / defaultPrice * 100))
                  : 0,
                createdAt: product.createdAt || "",
                updatedAt: product.updatedAt || "",
                tags: product.tags || ""
              };
            });
            
            return formattedProducts;
          } else {
            console.warn(`[디버그 ${requestTime}] 응답에 products 배열이 없거나 비어있음:`, data);
            return [];
          }
        } catch (error: any) {
          console.error(`[디버그 ${requestTime}] 에러 발생:`, error.message);
          throw error;
        } finally {
          // 캐시 관리 - 10초 후 제거 (캐시 시간 증가)
          setTimeout(() => {
            if (cacheKey in pendingApiCalls) {
              delete pendingApiCalls[cacheKey];
            }
          }, 10000);
        }
      })();
      
      return await pendingApiCalls[cacheKey];
    } catch (error: any) {
      console.error("[API 오류 발생]", error.message);
      return rejectWithValue(error.message || "상품 목록을 불러오는데 실패했습니다.");
    }
  }
);

// 상품 상세 정보 가져오기 액션 생성
export const fetchProductDetail = createAsyncThunk(
  'withcookie/fetchProductDetail',
  async (productId: number | string, { rejectWithValue }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      
      // productId가 문자열로 들어온 경우 숫자로 변환
      const numericProductId = typeof productId === 'string' ? parseInt(productId, 10) : productId;
      
      if (isNaN(numericProductId) || numericProductId <= 0) {
        console.error(`[API 오류] 유효하지 않은 상품 ID: ${productId}, 변환된 값: ${numericProductId}`);
        return rejectWithValue(`유효하지 않은 상품 ID: ${productId}`);
      }
      
      // API 엔드포인트 설정 (상품 ID 기반)
      const apiEndpoint = `${apiBaseUrl}/api/products/${numericProductId}`;
      
      const response = await fetch(apiEndpoint);
      
      if (!response.ok) {
        const errorText = await response.text().catch(() => '응답 텍스트 없음');
        console.error(`[API 오류] 상품 ID ${numericProductId} - 상태: ${response.status}, 오류: ${errorText}`);
        throw new Error(`상품 정보를 불러오는데 실패했습니다. 상태: ${response.status}, 오류: ${errorText}`);
      }
      
      const data = await response.json();
      
      // API 응답에서 product 객체 추출
      if (data && data.product) {
        
        // API 응답 전체를 그대로 반환 (원본 응답 구조 유지)
        return data;
      } else {
        console.warn('[유효한 데이터 없음] 응답에 product 객체가 없습니다.');
        return rejectWithValue("유효한 상품 데이터가 없습니다.");
      }
    } catch (error: any) {
      console.error("[API 오류 발생]", error.message);
      return rejectWithValue(error.message || "상품 정보를 불러오는데 실패했습니다.");
    }
  }
);

// 로그인 상태 확인 액션 생성
export const checkLoginStatus = createAsyncThunk(
  'withcookie/checkLoginStatus',
  async ({ forceRefresh = false }: { forceRefresh?: boolean } = {}, { getState, rejectWithValue }) => {
    try {
      // 이미 상태가 있고 강제 갱신이 아니면 기존 상태 재사용
      const state = getState() as { withcookie: WithcookieState };
      if (!forceRefresh && state.withcookie.user) {
        return { 
          isLoggedIn: state.withcookie.isUserLoggedIn, 
          user: state.withcookie.user,
          adminUser: state.withcookie.adminUser
        };
      }

      // 이미 진행 중인 API 호출이 있는지 확인
      const cacheKey = 'checkLoginStatus';
      if (cacheKey in pendingApiCalls && !forceRefresh) {
        return pendingApiCalls[cacheKey];
      }

      // 디바운싱 처리
      if (authCheckDebounceTimer && !forceRefresh) {
        clearTimeout(authCheckDebounceTimer);
      }

      const apiBaseUrl = getApiBaseUrl();
      
      // 요청 옵션 설정
      const requestOptions: RequestInit = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      };
      
      // 실제 API 호출 생성 및 저장
      pendingApiCalls[cacheKey] = new Promise(async (resolve) => {
        const response = await fetch(
          `${apiBaseUrl}/api/users/whoami`, // 수정 취소 - 이 경로는 이미 사용자 정보를 가져오는 올바른 경로
          requestOptions
        );

        if (!response.ok) {
          if (response.status >= 400) {
            resolve({ isLoggedIn: false, user: null, adminUser: null });
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        // 사용자 정보가 있으면 로그인된 상태로 설정
        const result = { 
          isLoggedIn: !!data.user, 
          user: data.user || null, 
          adminUser: data.administrator || null
        };
        
        resolve(result);
        
        // API 호출이 완료되면 캐시에서 제거 (짧은 지연 후)
        setTimeout(() => {
          delete pendingApiCalls[cacheKey];
        }, 200);
      });
      
      return pendingApiCalls[cacheKey];
    } catch (error: any) {
      console.error('[API 오류]', error);
      return rejectWithValue(error.message || '사용자 인증 확인 중 오류가 발생했습니다.');
    }
  }
);

// 장바구니 목록 조회 액션 생성
export const fetchCartItems = createAsyncThunk(
  'withcookie/fetchCartItems',
  async (_, { rejectWithValue, getState }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      
      // Redux 상태에서 로그인 여부 확인
      const state = getState() as { withcookie: WithcookieState };
      const isUserLoggedIn = state.withcookie.isUserLoggedIn;
      const isAdminLoggedIn = state.withcookie.isAdminLoggedIn;
      
      // 로그인 상태 로깅
      
      // 로그인 검증 - isUserLoggedIn 또는 isAdminLoggedIn 중 하나라도 true여야 함
      if (!isUserLoggedIn && !isAdminLoggedIn) {
        console.error('[장바구니 목록 조회 실패] 로그인 필요');
        return rejectWithValue({
          message: '로그인이 필요합니다.',
          code: 'AUTH_REQUIRED',
          statusCode: 401
        });
      }
      
      const apiEndpoint = `${apiBaseUrl}/api/carts`;
      
      // 요청 옵션 설정 - credentials: 'include'로 쿠키 전송
      const requestOptions: RequestInit = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include' // 쿠키 포함 설정
      };
      
      
      const response = await fetch(apiEndpoint, requestOptions);
      
      
      // 응답 데이터 확인
      const data = await response.json();
      
     
      
      // 401 상태 체크
      if (data.statusCode === 401) {
        return rejectWithValue({
          message: data.message || '로그인이 필요합니다.',
          code: 'AUTH_REQUIRED',
          statusCode: 401
        });
      }
      
      if (!response.ok) {
        return rejectWithValue({
          message: data.message || `장바구니 목록을 불러오는데 실패했습니다. 상태: ${response.status}`,
          statusCode: response.status
        });
      }
      
      // API 응답 구조에 맞게 처리 (cartItems가 아닌 carts 필드 사용)
      if (!data.carts) {
        return [];
      } else if (!Array.isArray(data.carts)) {
        // carts가 객체인 경우 처리
        if (typeof data.carts === 'object') {
          // 객체의 값들로 배열 생성
          return Object.values(data.carts);
        }
        return [];
      }
      
      return data.carts || [];
    } catch (error: any) {
      console.error('[API 오류]', error);
      return rejectWithValue({
        message: error.message || "장바구니 목록을 불러오는데 실패했습니다.",
        originalError: error,
        statusCode: 500,
        code: 'API_ERROR'
      });
    }
  }
);

// 장바구니 상품 삭제 액션 생성
export const removeFromCart = createAsyncThunk(
  'withcookie/removeFromCart',
  async (cartItemId: number, { rejectWithValue, getState }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      
      // Redux 상태에서 로그인 여부 확인
      const state = getState() as { withcookie: WithcookieState };
      const isUserLoggedIn = state.withcookie.isUserLoggedIn;
      const isAdminLoggedIn = state.withcookie.isAdminLoggedIn;
      
      
      // 로그인 검증 - isUserLoggedIn 또는 isAdminLoggedIn 중 하나라도 true여야 함
      if (!isUserLoggedIn && !isAdminLoggedIn) {
        console.error('[장바구니 삭제 실패] 로그인 필요');
        return rejectWithValue({
          message: '로그인이 필요합니다.',
          code: 'AUTH_REQUIRED'
        });
      }
      
      // API 엔드포인트
      const apiEndpoint = `${apiBaseUrl}/api/carts/${cartItemId}`;
      
      // 요청 옵션 설정 - credentials: 'include'로 쿠키 전송
      const requestOptions: RequestInit = {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'  // 쿠키 포함 설정
      };
      
      
      const response = await fetch(apiEndpoint, requestOptions);
      
      
      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.message || `장바구니에서 상품을 삭제하는데 실패했습니다. 상태: ${response.status}`);
      }
      
      return cartItemId;
    } catch (error: any) {
      console.error("[API 오류 발생]", error);
      return rejectWithValue({
        message: error.message || "장바구니에서 상품을 삭제하는데 실패했습니다.",
        originalError: error,
        code: 'API_ERROR'
      });
    }
  }
);

// 장바구니 상품 추가 액션 생성
export const addToCart = createAsyncThunk(
  'withcookie/addToCart',
  async ({ productId, count }: { productId: number; count: number }, { rejectWithValue, getState }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      
      // Redux 상태에서 로그인 여부 확인
      const state = getState() as { withcookie: WithcookieState };
      const isUserLoggedIn = state.withcookie.isUserLoggedIn;
      const isAdminLoggedIn = state.withcookie.isAdminLoggedIn;
      
      
      // 로그인 검증 - isUserLoggedIn 또는 isAdminLoggedIn 중 하나라도 true여야 함
      if (!isUserLoggedIn && !isAdminLoggedIn) {
        console.error('[장바구니 추가 실패] 로그인 필요');
        return rejectWithValue({
          message: '로그인이 필요합니다.',
          code: 'AUTH_REQUIRED'
        });
      }
      
      // API 엔드포인트
      const apiEndpoint = `${apiBaseUrl}/api/carts/register`;
      
      // 요청 옵션 설정 - credentials: 'include'로 쿠키 전송
      const requestOptions: RequestInit = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',  // 쿠키 포함 설정
        body: JSON.stringify({ productId, count })
      };
      
      
      
      const response = await fetch(apiEndpoint, requestOptions);
      
   
      
      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.message || `장바구니에 상품을 추가하는데 실패했습니다. 상태: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error("[API 오류 발생]", error);
      return rejectWithValue({
        message: error.message || "장바구니에 상품을 추가하는데 실패했습니다.",
        originalError: error,
        code: 'API_ERROR'
      });
    }
  }
);

// 로그아웃 액션 생성
export const logoutUser = createAsyncThunk(
  'withcookie/logoutUser',
  async (_, { rejectWithValue }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(
        `${apiBaseUrl}/api/auth/logout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include' // 쿠키를 포함하여 요청 (인증 정보 전송)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue({
          message: errorData.message || `로그아웃에 실패했습니다. 상태: ${response.status}`,
          errors: errorData.errors
        });
      }

      // 로컬 스토리지에서 토큰 제거 (필요시)
      if (localStorage.getItem('auth_token')) {
        localStorage.removeItem('auth_token');
      }

      return await response.json();
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || '로그아웃 중 오류가 발생했습니다.',
        errors: {}
      });
    }
  }
);

// 주소 검색 액션 생성
export const searchAddress = createAsyncThunk(
  'withcookie/searchAddress',
  async (query: string, { rejectWithValue }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(
        `${apiBaseUrl}/api/orders/find_addresses?query=${encodeURIComponent(query)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.addresses || [];
    } catch (error: any) {
      return rejectWithValue(error.message || '주소 검색 중 오류가 발생했습니다.');
    }
  }
);

// selectAddress 액션을 위한 부분을 Slice에 추가
export const selectAddress = createAsyncThunk(
  'withcookie/selectAddress',
  async (address: any, { dispatch }) => {
    // 선택한 주소를 저장
    dispatch(withcookieSlice.actions.setSelectedAddress(address));
    return address;
  }
);

// 결제 승인 액션 생성
export const confirmPayment = createAsyncThunk(
  'withcookie/confirmPayment',
  async ({ paymentKey, orderId, amount }: { paymentKey: string; orderId: string; amount: number }, { rejectWithValue }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(
        `${apiBaseUrl}/api/payments/confirm`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            paymentKey,
            orderId,
            amount
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue({
          message: errorData.message || `결제 승인에 실패했습니다. 상태: ${response.status}`,
          errors: errorData.errors
        });
      }

      return await response.json();
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || '결제 승인 중 오류가 발생했습니다.',
        errors: {}
      });
    }
  }
);

// 주문 등록 액션 생성
export const registerCheckout = createAsyncThunk(
  'withcookie/registerCheckout',
  async (orderData: {
    // 필수 사용자 정보
    user_id: number;
    company_id: number;
    skin_id: number;
    
    // 수신자 정보
    receiver_name: string;
    receiver_phone: string;
    receiver_email?: string;
    
    // 금액 정보
    total_price: number;
    buy_price: number;
    parcel_price: number;
    
    // 결제 방법
    payment_method: string;
    
    // 배송지 정보
    address: string;
    address_detail?: string;
    postal_code: string;
    
    // 장바구니 정보
    carts: Array<{
      product_id: number;
      company_id: number;
      user_id: number;
      count: number;
    }>;
  }, { rejectWithValue }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      
      // 필수 정보 검증
      if (!orderData.company_id) {
        console.error("[주문 등록 오류] company_id가 없습니다.");
        return rejectWithValue({
          message: '회사 정보가 없습니다. 다시 시도해주세요.',
          errors: { company_id: '회사 ID가 필요합니다.' }
        });
      }
      
      if (!orderData.carts || orderData.carts.length === 0) {
        console.error("[주문 등록 오류] 장바구니 항목이 없습니다.");
        return rejectWithValue({
          message: '구매할 상품을 골라주세요.',
          errors: { carts: '장바구니 항목이 필요합니다.' }
        });
      }
      
      
      const response = await fetch(
        `${apiBaseUrl}/api/checkouts/register`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify(orderData)
        }
      );
      if(response.status >= 400){
        const errorData = await response.json();
        return rejectWithValue({
          message: errorData.message
        });
      }

      // 헤더 로깅 - 타입 오류 해결
      const headers: Record<string, string> = {};
      response.headers.forEach((value, key) => {
        headers[key] = value;
      });

      // 201 Created 또는 200 OK는 성공 응답으로 처리
      if (response.status === 201 || response.status === 200) {
        
        try {
          // 응답 본문이 있는지 확인 (비어있을 수 있음)
          const responseText = await response.text();
          
          let responseData: Record<string, any> = {};
          
          // 응답 본문이 있고 JSON 형식이면 파싱
          if (responseText && responseText.trim()) {
            try {
              responseData = JSON.parse(responseText);
            } catch (parseError) {
              console.warn("[주문 등록 응답] JSON 파싱 실패, 원본 텍스트 사용:", parseError);
              // JSON 파싱 실패 시 기본 응답 구성
              responseData = {
                statusCode: response.status,
                message: response.statusText || "주문이 등록되었습니다.",
                checkout: {
                  id: Date.now(),
                  status: "REQUESTED"
                }
              };
            }
          } else {
            // 응답 본문이 비어있는 경우 기본 응답 구성
            responseData = {
              statusCode: response.status,
              message: response.statusText || "주문이 등록되었습니다.",
              checkout: {
                id: Date.now(),
                status: "REQUESTED"
              }
            };
          }
          
          // 성공 응답 반환 (원본 서버 응답 그대로 반환)
          return responseData;
        } catch (parseError) {
          console.error("[주문 등록 응답] 응답 처리 오류:", parseError);
          
          // 파싱 오류가 발생해도 201/200 코드는 성공이므로 성공 응답 반환
          return {
            statusCode: response.status,
            message: "주문이 등록되었습니다.",
            checkout: {
              id: Date.now(),
              status: "REQUESTED"
            }
          };
        }
      }
    } catch (error: any) {
      console.error("[주문 등록 예외]", error);
      return rejectWithValue({
        message: error.message || '주문 등록 중 오류가 발생했습니다.',
        errors: {},
        statusCode: 500
      });
    }
  }
);

// 토스페이먼츠 결제 확인 액션 생성
export const confirmTossPayment = createAsyncThunk(
  'withcookie/confirmTossPayment',
  async ({ 
    checkout_id,
    user_id,
    company_id,
    skin_id,
    toss_request
  }: { 
    checkout_id: number;
    user_id: number;
    company_id: number;
    skin_id: number;
    toss_request: {
      paymentKey: string;
      orderId: string;
      paymentType: string;
      amount: number;
    }
  }, { rejectWithValue }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      
      // 필수 파라미터 검증
      if (!checkout_id) {
        console.error("[결제 확인 오류] checkout_id가 없습니다.");
        return rejectWithValue({
          message: '주문 정보가 없습니다. 다시 시도해주세요.',
          errors: { checkout_id: '주문 ID가 필요합니다.' }
        });
      }
      
      if (!company_id) {
        console.error("[결제 확인 오류] company_id가 없습니다.");
        return rejectWithValue({
          message: '회사 정보가 없습니다. 다시 시도해주세요.',
          errors: { company_id: '회사 ID가 필요합니다.' }
        });
      }
      
      if (!toss_request || !toss_request.paymentKey) {
        console.error("[결제 확인 오류] 결제 정보가 없습니다.");
        return rejectWithValue({
          message: '결제 정보가 유효하지 않습니다. 다시 시도해주세요.',
          errors: { toss_request: '결제 정보가 필요합니다.' }
        });
      }
      
     
      
      const response = await fetch(
        `${apiBaseUrl}/api/checkouts/toss_confirm_request`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            checkout_id,
            user_id,
            company_id,
            skin_id,
            toss_request
          })
        }
      );

      // 응답 상태 및 데이터 로깅
      
      const responseData = await response.json();

      if (!response.ok) {
        return rejectWithValue({
          message: responseData.message || `결제 확인에 실패했습니다. 상태: ${response.status}`,
          errors: responseData.errors || {},
          statusCode: response.status
        });
      }

      return responseData;
    } catch (error: any) {
      console.error("[결제 확인 예외]", error);
      return rejectWithValue({
        message: error.message || '결제 확인 중 오류가 발생했습니다.',
        errors: {},
        statusCode: 500
      });
    }
  }
);

// 장바구니 상품 수량 업데이트 액션 생성
export const updateCartQuantity = createAsyncThunk(
  'withcookie/updateCartQuantity',
  async ({ cartItemId, quantity }: { cartItemId: number; quantity: number }, { rejectWithValue, getState }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      
      // Redux 상태에서 로그인 여부 확인
      const state = getState() as { withcookie: WithcookieState };
      const isUserLoggedIn = state.withcookie.isUserLoggedIn;
      const isAdminLoggedIn = state.withcookie.isAdminLoggedIn;
      
      
      // 로그인 검증 - isUserLoggedIn 또는 isAdminLoggedIn 중 하나라도 true여야 함
      if (!isUserLoggedIn && !isAdminLoggedIn) {
        console.error('[장바구니 수량 업데이트 실패] 로그인 필요');
        return rejectWithValue({
          message: '로그인이 필요합니다.',
          code: 'AUTH_REQUIRED'
        });
      }
      
      // API 엔드포인트
      const apiEndpoint = `${apiBaseUrl}/api/carts/update`;
      
      // 요청 옵션 설정 - credentials: 'include'로 쿠키 전송
      const requestOptions: RequestInit = {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',  // 쿠키 포함 설정
        body: JSON.stringify({ cartItemId, count: quantity })
      };
      
      
      
      const response = await fetch(apiEndpoint, requestOptions);
      
     
      
      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.message || `장바구니 수량 업데이트에 실패했습니다. 상태: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error("[API 오류 발생]", error);
      return rejectWithValue({
        message: error.message || "장바구니 수량 업데이트에 실패했습니다.",
        originalError: error,
        code: 'API_ERROR'
      });
    }
  }
);

// 주문 내역 가져오기 액션
export const fetchCheckouts = createAsyncThunk(
  'withcookie/fetchCheckouts',
  async ({ take = 100, page = 1 }: { take?: number; page?: number }, { rejectWithValue, getState }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      
      // Redux 상태에서 로그인 여부 확인
      const state = getState() as { withcookie: WithcookieState };
      const isUserLoggedIn = state.withcookie.isUserLoggedIn;
      const isAdminLoggedIn = state.withcookie.isAdminLoggedIn;
      
     
      
      // 로그인 검증 - isUserLoggedIn 또는 isAdminLoggedIn 중 하나라도 true여야 함
      if (!isUserLoggedIn && !isAdminLoggedIn) {
        console.error('[주문 내역 조회 실패] 로그인 필요');
        return rejectWithValue({
          message: '로그인이 필요합니다.',
          code: 'AUTH_REQUIRED'
        });
      }
      
      // API 엔드포인트 및 쿼리 파라미터
      const apiEndpoint = `${apiBaseUrl}/api/checkouts?take=${take}&page=${page}`;
      console.log(`[체크아웃 API 요청] ${apiEndpoint}`);
      
      // 요청 옵션 설정 - credentials: 'include'로 쿠키 전송
      const requestOptions: RequestInit = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include' // 쿠키 포함 설정
      };
      
      
      const response = await fetch(apiEndpoint, requestOptions);
      
     
      
      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.message || `주문 내역을 가져오는데 실패했습니다. 상태: ${response.status}`);
      }
      
      const data = await response.json();
      
      // API 응답 로깅
      console.log('[체크아웃 API 응답 상세]', {
        status: response.status,
        checkouts: data.checkouts ? data.checkouts.length : 0,
        totalCount: data.totalCount,
        total: data.total,
        pagination: data.pagination,
        currentPage: page,
        take: take,
        계산된페이지수: Math.ceil((data.totalCount || data.total || data.checkouts?.length || 0) / take)
      });
     
      
      return {
        checkouts: data.checkouts || [],
        pagination: data.pagination || {
          currentPage: page,
          totalPages: Math.ceil((data.totalCount || data.total || data.checkouts?.length || 0) / take),
          totalItems: data.totalCount || data.total || data.checkouts?.length || 0
        }
      };
    } catch (error: any) {
      console.error("[API 오류 발생]", error.message);
      return rejectWithValue({
        message: error.message || "주문 내역을 가져오는데 실패했습니다.",
        originalError: error,
        code: 'API_ERROR'
      });
    }
  }
);

// 바이너리 트리 데이터를 가져오는 액션 생성
export const fetchBinaryTree = createAsyncThunk(
  'withcookie/fetchBinaryTree',
  async ({ id, depth = 10, one_upper = false }: { id?: string | number, depth?: string | number, one_upper?: boolean } = {}, { rejectWithValue, getState }) => {
    try {
      // 사용자 ID 확인
      let userId = id;
      if (!userId) {
        const state = getState() as { withcookie: WithcookieState };
        const user = state.withcookie.user || state.withcookie.userInfo;
        
        if (user && user.id) {
          userId = String(user.id);
        } else {
          return rejectWithValue('사용자 ID가 없습니다. 로그인이 필요합니다.');
        }
      }
      
      const apiBaseUrl = getApiBaseUrl();
      const endpoint = `${apiBaseUrl}/api/users/get_binary_tree`;
      // depth를 문자열로 변환하여 요청
      const requestBody: Record<string, any> = { 
        id: String(userId), 
        depth: String(depth)
      };
      if (userId){
        const state = getState() as { withcookie: WithcookieState };
        const user = state.withcookie.user || state.withcookie.userInfo;
        requestBody.requestUserId = String(user.id);
      }


      // one_upper 값이 true인 경우에만 추가
      if (one_upper === true) {
        requestBody.one_upper = true;
      }
      
      
      const response = await fetch(
        endpoint,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify(requestBody)
        }
      );
      
     
      
      if (!response.ok) {
        throw new Error(`API 오류: ${response.status}`);
      }
      
      // 응답 데이터를 컴포넌트에 맞게 변환
      const data = await response.json();
      
      
      
      // 데이터 변환 함수 - API 응답 필드를 컴포넌트가 기대하는 필드로 매핑
      const transformNode = (node: any) => {
        if (!node) return null;
        
        return {
          ...node,
          // title1을 title로 매핑
          title: node.title1 ? node.title1.replace('성함: ', '') : node.title || node.name,
          // class를 isShipper로 매핑 (조건부)
          isShipper: node.class === 'shipper',
          // 자식 노드들도 재귀적으로 변환
          children: Array.isArray(node.children) 
            ? node.children.map(transformNode) 
            : []
        };
      };
      
      return transformNode(data);
    } catch (error: any) {
      console.error('[바이너리 트리] API 오류:', error);
      return rejectWithValue(error.message || '바이너리 트리 데이터를 가져오는 중 오류가 발생했습니다.');
    }
  }
);

// 팝업 데이터 가져오기 액션
export const fetchPopupPosts = createAsyncThunk(
  'withcookie/fetchPopupPosts',
  async (_, { rejectWithValue, getState }) => {
    try {
      // 이미 상태에 팝업 데이터가 있는지 확인
      const state = getState() as { withcookie: WithcookieState };
      const popups = state.withcookie.popups;
      
      // 이미 팝업 데이터가 있으면 API 호출하지 않고 기존 데이터 반환
      if (Array.isArray(popups) && popups.length > 0) {
        return popups;
      }
      
      // 이미 진행 중인 API 호출이 있는지 확인 (pendingApiCalls 체크)
      if ('popupPosts' in pendingApiCalls) {
        return pendingApiCalls['popupPosts'];
      }
      
      const apiBaseUrl = getApiBaseUrl();
      
      // 프로미스 생성 및 pendingApiCalls에 등록
      const fetchPromise = (async () => {
        try {
          // 실제 API 호출 실행
          const response = await fetch(
            `${apiBaseUrl}/api/posts?category_code=ADDITIONAL_POPUP`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json'
              },
              credentials: 'include'
            }
          );
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const data = await response.json();
          
          // 데이터 형식 변환
          const transformedPopups = data.posts 
            ? data.posts.map((post: any) => ({
                ...post,
                links: post.links ? {
                  imgUrl: post.links[0] || '',
                  textAlignment: post.links[1] || 'flex-start',
                  textJustify: post.links[2] || 'flex-start',
                  textColor: post.links[3] || '#000000',
                  backgroundColor: post.links[4] || '#ffffff',
                  popupPadding: post.links[5] || '20',
                  link: post.links[6] || '',
                } : {
                  imgUrl: '',
                  textAlignment: 'flex-start',
                  textJustify: 'flex-start',
                  textColor: '#000000',
                  backgroundColor: '#ffffff',
                  popupPadding: '20',
                  link: '',
                }
              }))
            : [];
          
          return transformedPopups;
        } catch (error) {
          console.error('[팝업 데이터] API 오류:', error);
          throw error;
        } finally {
          // API 호출 완료 후 pendingApiCalls에서 제거
          setTimeout(() => {
            if ('popupPosts' in pendingApiCalls) {
              delete pendingApiCalls['popupPosts'];
            }
          }, 1000);
        }
      })();
      
      // 프로미스를 pendingApiCalls에 등록
      pendingApiCalls['popupPosts'] = fetchPromise;
      
      // 프로미스 반환
      return fetchPromise;
    } catch (error: any) {
      console.error('[팝업 데이터] 오류:', error);
      return rejectWithValue(error.message || '팝업 데이터를 가져오는 중 오류가 발생했습니다.');
    }
  }
);

// 다국어 사전 가져오기 액션
export const fetchTranslationDictionary = createAsyncThunk(
  'withcookie/fetchTranslationDictionary',
  async (_, { rejectWithValue, getState }) => {
    try {
      // 이미 상태에 데이터가 있는지 확인
      const state = getState() as { withcookie: WithcookieState };
      const dictionary = state.withcookie.translationDictionary;
      
      // 이미 데이터가 있으면 API 호출하지 않고 기존 데이터 반환
      if (Object.keys(dictionary).length > 0) {
        return dictionary;
      }
      
      // 이미 진행 중인 API 호출이 있는지 확인
      const cacheKey = 'fetchTranslationDictionary';
      if (cacheKey in pendingApiCalls) {
        return pendingApiCalls[cacheKey];
      }
      
      const API_SERVER_URL = window.location.origin; // 개발 환경에서는 5001 포트 API 서버 사용
      
      // API 호출 시작
      pendingApiCalls[cacheKey] = new Promise(async (resolve) => {
        try {
          // const apiBaseUrl = getApiBaseUrl(); // 기존 코드
          const response = await fetch(
            `${API_SERVER_URL}/api/translation-dictionary/get`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json'
              },
              // 개발 환경에서는 include 대신 same-origin 사용 (CORS 이슈 해결)
              credentials: process.env.NODE_ENV === 'production' ? 'include' : 'same-origin'
            }
          );
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const data = await response.json();
          
          resolve(data);
        } catch (error) {
          console.error('[다국어 사전 로드 실패]', error);
          resolve({});
        } finally {
          // 짧은 지연 후 캐시에서 제거
          setTimeout(() => {
            delete pendingApiCalls[cacheKey];
          }, 200);
        }
      });
      
      return pendingApiCalls[cacheKey];
    } catch (error: any) {
      console.error('[다국어 사전 로드 오류]', error);
      return rejectWithValue(error.message || '다국어 사전을 불러오는 중 오류가 발생했습니다.');
    }
  }
);

// 리뷰 등록 액션 생성
export const createReview = createAsyncThunk(
  'withcookie/createReview',
  async (reviewData: { productId: number; cartId: number; rating: number; content: string }, { rejectWithValue }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const apiUrl = `${apiBaseUrl}/api/reviews`;
      
      // 요청 데이터 로깅
      
      
      const response = await fetch(
        apiUrl,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify(reviewData)
        }
      );

      // 응답 상태 코드 로깅
      
      if (!response.ok) {
        // 응답이 JSON이 아닐 수 있으므로 먼저 텍스트로 읽습니다.
        const responseText = await response.text();
        console.error('[리뷰 등록 실패] 응답 텍스트:', responseText);
        
        let errorData;
        try {
          // JSON으로 파싱 시도
          errorData = JSON.parse(responseText);
        } catch (parseError) {
          // JSON 파싱 실패시 텍스트 그대로 사용
          console.error('[리뷰 등록 실패] JSON 파싱 실패:', parseError);
          return rejectWithValue({
            message: `리뷰 등록에 실패했습니다. 상태: ${response.status}`,
            responseText: responseText.substring(0, 200) // 텍스트가 너무 길 수 있으므로 일부만 포함
          });
        }
        
        return rejectWithValue({
          message: errorData.message || `리뷰 등록에 실패했습니다. 상태: ${response.status}`,
          errors: errorData.errors || errorData
        });
      }

      // 성공 응답 처리 - 먼저 텍스트로 읽고 JSON 파싱
      const responseText = await response.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('[리뷰 등록 성공] JSON 파싱 실패:', parseError);
        return rejectWithValue({
          message: '서버 응답을 처리하는데 실패했습니다',
          responseText: responseText.substring(0, 200)
        });
      }
      
      return data;
    } catch (error: any) {
      console.error('[리뷰 등록 예외]', error);
      return rejectWithValue({
        message: error.message || '리뷰 등록 중 오류가 발생했습니다.',
        errors: {}
      });
    }
  }
);

// 사용자 정보 수정 액션 생성
export const updateUserProfile = createAsyncThunk(
  'withcookie/updateUserProfile',
  async (userData: {
    name?: string;
    phone?: string;
    birthday?: string;
    email?: string;
    address?: string;
    password?: string;
    var01?: string;
    var02?: string;
    var03?: string;
  }, { rejectWithValue, getState }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      
      // Redux 상태에서 사용자 정보와 회사 ID 가져오기
      const state = getState() as { withcookie: WithcookieState };
      const user = state.withcookie.user;
      
      if (!user || !user.id) {
        return rejectWithValue({
          message: '로그인이 필요합니다.',
          code: 'AUTH_REQUIRED'
        });
      }
      
      // 회사 ID 가져오기
      const companyId = user.company_id || 21; // 기본값으로 21 사용
      
      // FormData 객체 생성
      const formData = new URLSearchParams();
      
      // 사용자 정보 설정
      if (userData.name !== undefined) formData.append('name', userData.name);
      if (userData.phone !== undefined) formData.append('phone', userData.phone);
      if (userData.birthday !== undefined) formData.append('birthday', userData.birthday);
      if (userData.email !== undefined) formData.append('email', userData.email);
      if (userData.address !== undefined) formData.append('address', userData.address);
      if (userData.password !== undefined && userData.password.trim() !== '') formData.append('password', userData.password);
      if (userData.var01 !== undefined) formData.append('var01', userData.var01);
      if (userData.var02 !== undefined) formData.append('var02', userData.var02);
      if (userData.var03 !== undefined) formData.append('var03', userData.var03);
      
      // API 요청
      const response = await fetch(
        `${apiBaseUrl}/api/users/${user.id}?company_id=${companyId}`,
        {
          method: 'PUT',
          headers: {
            'Accept': 'application/json, text/plain, */*',
            'Content-Type': 'application/x-www-form-urlencoded',
            'Logipasta-Platform': 'admin'
          },
          credentials: 'include',
          body: formData.toString()
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: '사용자 정보 업데이트 실패' }));
        return rejectWithValue({
          message: errorData.message || `사용자 정보 업데이트 실패: ${response.status}`,
          statusCode: response.status
        });
      }
      
      // 응답 데이터 처리
      const data = await response.json();
      return data.user || data;
    } catch (error: any) {
      console.error("[API 오류 발생]", error);
      return rejectWithValue({
        message: error.message || "사용자 정보 업데이트 중 오류가 발생했습니다.",
        originalError: error,
        code: 'API_ERROR'
      });
    }
  }
);

// 사용자 정보를 ID로 조회하는 액션 생성
export const getUserById = createAsyncThunk(
  'withcookie/getUserById',
  async ({ userId, companyId = 21 }: { userId: number; companyId?: number }, { rejectWithValue }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      
      // API 요청
      const response = await fetch(
        `${apiBaseUrl}/api/users/${userId}?company_id=${companyId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json, text/plain, */*',
            'Content-Type': 'application/json',
            'Logipasta-Platform': 'admin',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
          credentials: 'include'
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: '사용자 정보 조회 실패' }));
        return rejectWithValue({
          message: errorData.message || `사용자 정보 조회 실패: ${response.status}`,
          statusCode: response.status
        });
      }
      
      // 응답 데이터 처리
      const data = await response.json();
      
      if (!data.user) {
        return rejectWithValue({
          message: '사용자 정보가 응답에 포함되지 않았습니다.',
          statusCode: 404
        });
      }
      
      return data.user;
    } catch (error: any) {
      console.error("[API 오류 발생]", error);
      return rejectWithValue({
        message: error.message || "사용자 정보 조회 중 오류가 발생했습니다.",
        originalError: error,
        code: 'API_ERROR'
      });
    }
  }
);

export const withcookieSlice = createSlice({
  name: 'withcookie',
  initialState,
  reducers: {
    setWithcookieData: (state, action: PayloadAction<any>) => {
      state.withcookieData = action.payload;
    },
    clearWithcookieData: (state) => {
      state.withcookieData = null;
    },
    setSavedProjects: (state, action: PayloadAction<any>) => {
      state.savedProjects = action.payload;
    },
    // 장바구니 아이템 직접 설정
    setCartItems: (state, action: PayloadAction<any[]>) => {
      state.cartItems = action.payload;
    },
    resetLoginState: (state) => {
      state.loginSuccess = false;
      state.loginError = null;
      state.validationErrors = {};
    },
    resetSignUpState: (state) => {
      state.signUpSuccess = false;
      state.signUpError = null;
      state.validationErrors = {};
    },
    resetProductsState: (state) => {
      state.products = [];
      state.productsError = null;
    },
    resetProductDetailState: (state) => {
      state.productDetail = null;
      state.productDetailError = null;
    },
    setUser: (state, action: PayloadAction<any>) => {
      state.user = action.payload;
      state.isUserLoggedIn = !!action.payload;
    },
    clearUser: (state) => {
      state.user = null;
      state.isUserLoggedIn = false;
    },
    setAdminUser: (state, action: PayloadAction<any>) => {
      state.adminUser = action.payload;
      state.isAdminLoggedIn = !!action.payload;
    },
    clearAdminUser: (state) => {
      state.adminUser = null;
      state.isAdminLoggedIn = false;
    },
    logout: (state) => {
      state.user = null;
      state.adminUser = null;
      state.isUserLoggedIn = false;
      state.isAdminLoggedIn = false;
      state.isLoggedIn = false;
      state.cartItems = [];
    },
    setSelectedAddress: (state, action: PayloadAction<any>) => {
      state.selectedAddress = action.payload;
    },
    setWithcookieDataLastLoaded: (state, action: PayloadAction<number>) => {
      state.withcookieDataLastLoaded = action.payload;
    },
    // 메뉴 관련 액션 추가
    setGlobalMenus: (state, action: PayloadAction<any[]>) => {
      state.globalMenus = action.payload;
    },
    setMainMenus: (state, action: PayloadAction<any[]>) => {
      state.mainMenus = action.payload;
    },
    clearMenus: (state) => {
      state.globalMenus = [];
      state.mainMenus = [];
    },
    // 결제 실패 정보 설정 - 일반 액션으로 변경
    setPaymentFailure: (state, action: PayloadAction<{
      errorCode?: string;
      errorMessage?: string;
      orderId?: string;
    }>) => {
      state.paymentFailure.errorCode = action.payload.errorCode || null;
      state.paymentFailure.errorMessage = action.payload.errorMessage || null;
      state.paymentFailure.orderId = action.payload.orderId || null;
    },
    // 결제 실패 정보 초기화 - 일반 액션으로 변경
    clearPaymentFailure: (state) => {
      state.paymentFailure.errorCode = null;
      state.paymentFailure.errorMessage = null;
      state.paymentFailure.orderId = null;
    },
    setCompanyIsUse: (state, action: PayloadAction<boolean>) => {
      state.companyIsUse = action.payload;
    },
    // 다국어 설정 액션 추가
    setCurrentLanguage: (state, action: PayloadAction<string>) => {
      state.currentLanguage = action.payload;
    },
    // 다국어 사전 직접 업데이트 액션 추가
    setTranslationDictionary: (state, action: PayloadAction<Record<string, Record<string, string>>>) => {
      state.translationDictionary = action.payload;
    },
    // 리뷰 상태 초기화 액션 추가
    resetReviewState: (state) => {
      state.reviewLoading = false;
      state.reviewError = null;
      state.reviewSuccess = false;
    },
    // company 정보 설정 액션 추가
    setCompany: (state, action: PayloadAction<any>) => {
      state.company = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveMenuJsonToServer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveMenuJsonToServer.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(saveMenuJsonToServer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || null;
      })

      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.loginError = null;
        state.loginSuccess = false;
        state.validationErrors = {};
      })
      .addCase(loginUser.fulfilled, (state) => {
        state.loading = false;
        state.loginSuccess = true;
        state.loginError = null;
      })
      .addCase(loginUser.rejected, (state, action: any) => {
        state.loading = false;
        state.loginSuccess = false;
        state.loginError = action.payload?.message || '로그인에 실패했습니다.';
        state.validationErrors = action.payload?.errors || {};
      })
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.signUpError = null;
        state.signUpSuccess = false;
        state.validationErrors = {};
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.loading = false;
        state.signUpSuccess = true;
        state.signUpError = null;
      })
      .addCase(registerUser.rejected, (state, action: any) => {
        state.loading = false;
        state.signUpSuccess = false;
        state.signUpError = action.payload?.message || '회원가입에 실패했습니다.';
        state.validationErrors = action.payload?.errors || {};
      })
      .addCase(fetchProducts.pending, (state) => {
        state.productsLoading = true;
        state.productsError = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.productsLoading = false;
        state.products = action.payload;
        state.productsError = null;
      })
      .addCase(fetchProducts.rejected, (state, action: any) => {
        state.productsLoading = false;
        state.productsError = action.payload || "상품 목록을 불러오는데 실패했습니다.";
      })
      .addCase(fetchProductDetail.pending, (state) => {
        state.productDetailLoading = true;
        state.productDetailError = null;
      })
      .addCase(fetchProductDetail.fulfilled, (state, action) => {
        state.productDetailLoading = false;
        state.productDetail = action.payload;
        state.productDetailError = null;
      })
      .addCase(fetchProductDetail.rejected, (state, action: any) => {
        state.productDetailLoading = false;
        state.productDetailError = action.payload || "상품 정보를 불러오는데 실패했습니다.";
      })
      .addCase(checkAdminToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkAdminToken.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.adminUser = action.payload.adminUser;
        state.isAdminLoggedIn = action.payload.isLoggedIn;
      })
      .addCase(checkAdminToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || null;
        state.adminUser = null;
        state.isAdminLoggedIn = false;
      })
      .addCase(checkLoginStatus.pending, (state) => {
        state.userInfoLoading = true;
        state.userInfoError = null;
      })
      .addCase(checkLoginStatus.fulfilled, (state, action) => {
        state.userInfoLoading = false;
        
        
        // API 응답에서 user 객체 추출
        const userData = action.payload?.user;
        
        // userInfo에 API 응답 전체가 아닌 사용자 객체만 저장
        state.userInfo = userData || null;
        state.userInfoError = null;
        
        // user 객체가 있으면 isLoggedIn과 isUserLoggedIn을 true로 설정
        if (userData) {
          state.isLoggedIn = true;
          state.user = userData;
          state.isUserLoggedIn = true;
          
          
        } else {
          // user 객체가 없으면 isUserLoggedIn = false 설정
          state.isUserLoggedIn = false;
          state.user = null;
        }
        
        // administrator 객체가 있으면 isAdminLoggedIn = true 설정
        if (action.payload && action.payload.adminUser) {
          state.adminUser = action.payload.adminUser;
          state.isAdminLoggedIn = true;
          
          
        } else {
          // administrator 객체가 없으면 isAdminLoggedIn = false 설정
          state.isAdminLoggedIn = false;
          state.adminUser = null;
        }
      })
      .addCase(checkLoginStatus.rejected, (state, action: any) => {
        state.userInfoLoading = false;
        state.userInfoError = action.payload || "로그인 상태 확인에 실패했습니다.";
        state.userInfo = null;
        state.isLoggedIn = false;
        state.user = null;
        state.isUserLoggedIn = false;
        state.adminUser = null;
        state.isAdminLoggedIn = false;
        
        
      })
      .addCase(fetchCartItems.pending, (state) => {
        state.cartLoading = true;
        state.cartError = null;
      })
      .addCase(fetchCartItems.fulfilled, (state, action) => {
        state.cartLoading = false;
        state.cartItems = action.payload;
        state.cartError = null;
      })
      .addCase(fetchCartItems.rejected, (state, action: any) => {
        state.cartLoading = false;
        state.cartError = action.payload || "장바구니 목록을 불러오는데 실패했습니다.";
      })
      .addCase(removeFromCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeFromCart.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(removeFromCart.rejected, (state, action: any) => {
        state.loading = false;
        state.cartLoading = false;
        state.cartError = action.payload || '장바구니 항목 삭제 중 오류가 발생했습니다.';
      })
      .addCase(addToCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToCart.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(addToCart.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload || "장바구니에 상품을 추가하는데 실패했습니다.";
      })
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        state.user = null;
        state.adminUser = null;
        state.isUserLoggedIn = false;
        state.isAdminLoggedIn = false;
        state.isLoggedIn = false;
        state.cartItems = [];
      })
      .addCase(logoutUser.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload?.message || '로그아웃에 실패했습니다.';
      })
      // 주소 검색 액션 처리
      .addCase(searchAddress.pending, (state) => {
        state.addressLoading = true;
        state.addressError = null;
      })
      .addCase(searchAddress.fulfilled, (state, action) => {
        state.addressLoading = false;
        state.addressResults = action.payload;
      })
      .addCase(searchAddress.rejected, (state, action: any) => {
        state.addressLoading = false;
        state.addressError = action.payload || '주소 검색 중 오류가 발생했습니다.';
      })
      .addCase(confirmPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(confirmPayment.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(confirmPayment.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload?.message || '결제 승인 중 오류가 발생했습니다.';
      })
      .addCase(registerCheckout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerCheckout.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(registerCheckout.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload || "주문 등록에 실패했습니다.";
      })
      .addCase(confirmTossPayment.pending, (state) => {
        state.paymentConfirmation.loading = true;
        state.paymentConfirmation.error = null;
        state.paymentConfirmation.data = null;
        state.paymentConfirmation.success = false;
      })
      .addCase(confirmTossPayment.fulfilled, (state, action) => {
        state.paymentConfirmation.loading = false;
        state.paymentConfirmation.error = null;
        state.paymentConfirmation.data = action.payload;
        state.paymentConfirmation.success = true;
      })
      .addCase(confirmTossPayment.rejected, (state, action: any) => {
        state.paymentConfirmation.loading = false;
        state.paymentConfirmation.error = action.payload?.message || "토스 결제 확인에 실패했습니다.";
        state.paymentConfirmation.data = action.payload;
        state.paymentConfirmation.success = false;
      })
      // 글로벌 및 주 메뉴 저장 액션 처리
      .addCase(saveMenus.pending, (state) => {
        state.menuLoading = true;
        state.menuError = null;
      })
      .addCase(saveMenus.fulfilled, (state, action) => {
        state.menuLoading = false;
        state.menuError = null;
        
      })
      .addCase(saveMenus.rejected, (state, action: any) => {
        state.menuLoading = false;
        state.menuError = action.payload || '메뉴 저장 중 오류가 발생했습니다.';
      })
      // 글로벌 및 주 메뉴 로드 액션 처리
      .addCase(loadMenus.pending, (state) => {
        state.menuLoading = true;
        state.menuError = null;
      })
      .addCase(loadMenus.fulfilled, (state, action) => {
        state.menuLoading = false;
        state.menuError = null;
        
        if (action.payload) {
          state.globalMenus = action.payload.globalMenus || [];
          state.mainMenus = action.payload.mainMenus || [];
        }
      })
      .addCase(loadMenus.rejected, (state, action: any) => {
        state.menuLoading = false;
        state.menuError = action.payload || '메뉴 로드 중 오류가 발생했습니다.';
      })
      .addCase(fetchCheckouts.pending, (state) => {
        state.checkoutsLoading = true;
        state.checkoutsError = null;
      })
      .addCase(fetchCheckouts.fulfilled, (state, action) => {
        state.checkoutsLoading = false;
        state.checkouts = action.payload.checkouts || [];
        state.checkoutsPagination = action.payload.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0
        };
      })
      .addCase(fetchCheckouts.rejected, (state, action: any) => {
        state.checkoutsLoading = false;
        state.checkoutsError = action.payload || "주문 내역을 가져오는데 실패했습니다.";
      })
      // 바이너리 트리 액션에 대한 리듀서 추가
      .addCase(fetchBinaryTree.pending, (state) => {
        state.binaryTreeLoading = true;
        state.binaryTreeError = null;
      })
      .addCase(fetchBinaryTree.fulfilled, (state, action) => {
        state.binaryTreeLoading = false;
        state.binaryTreeData = action.payload;
      })
      .addCase(fetchBinaryTree.rejected, (state, action) => {
        state.binaryTreeLoading = false;
        state.binaryTreeError = action.payload as string;
      })
      // 팝업 데이터 가져오기 액션 처리
      .addCase(fetchPopupPosts.pending, (state) => {
        
        state.popupsLoading = true;
        state.popupsError = null;
      })
      .addCase(fetchPopupPosts.fulfilled, (state, action) => {
        state.popupsLoading = false;
        state.popups = action.payload;
      })
      .addCase(fetchPopupPosts.rejected, (state, action) => {
        state.popupsLoading = false;
        state.popupsError = action.payload as string;
      })
      // 게시판 목록 조회
      .addCase(fetchBoards.pending, (state) => {
        state.boardsLoading = true;
        state.boardsError = null;
      })
      .addCase(fetchBoards.fulfilled, (state, action) => {
        state.boardsLoading = false;
        state.boards = action.payload;
      })
      .addCase(fetchBoards.rejected, (state, action) => {
        state.boardsLoading = false;
        state.boardsError = action.payload as string;
      })
      // 게시글 목록 조회
      .addCase(fetchPosts.pending, (state) => {
        state.postsLoading = true;
        state.postsError = null;
      })
      .addCase(fetchPosts.fulfilled, (state, action) => {
        state.postsLoading = false;
        state.posts = action.payload.posts;
        state.postsPagination = {
          currentPage: action.payload.page,
          totalPages: Math.ceil(action.payload.totalCount / action.payload.perPage),
          totalCount: action.payload.totalCount
        };
      })
      .addCase(fetchPosts.rejected, (state, action) => {
        state.postsLoading = false;
        state.postsError = action.payload as string;
      })
      // 게시글 상세 조회
      .addCase(fetchPostDetail.pending, (state) => {
        state.postDetailLoading = true;
        state.postDetailError = null;
      })
      .addCase(fetchPostDetail.fulfilled, (state, action) => {
        state.postDetailLoading = false;
        state.postDetail = action.payload;
      })
      .addCase(fetchPostDetail.rejected, (state, action) => {
        state.postDetailLoading = false;
        state.postDetailError = action.payload as string;
      })
      // 게시글 생성
      .addCase(createPost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPost.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createPost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // 게시글 수정
      .addCase(updatePost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePost.fulfilled, (state, action) => {
        state.loading = false;
        if (state.postDetail && state.postDetail.id === action.payload.id) {
          state.postDetail = action.payload;
        }
      })
      .addCase(updatePost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // 게시글 삭제
      .addCase(deletePost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePost.fulfilled, (state, action) => {
        state.loading = false;
        state.posts = state.posts.filter(post => post.id !== action.payload);
        if (state.postDetail && state.postDetail.id === action.payload) {
          state.postDetail = null;
        }
      })
      .addCase(deletePost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // 댓글 작성
      .addCase(createComment.fulfilled, (state, action) => {
        if (state.postDetail) {
          if (!state.postDetail.children) {
            state.postDetail.children = [];
          }
          state.postDetail.children.push(action.payload);
        }
      })
      // 다국어 사전 가져오기 액션 처리
      .addCase(fetchTranslationDictionary.pending, (state) => {
        state.translationLoading = true;
        state.translationError = null;
      })
      .addCase(fetchTranslationDictionary.fulfilled, (state, action) => {
        state.translationLoading = false;
        state.translationDictionary = action.payload;
      })
      .addCase(fetchTranslationDictionary.rejected, (state, action) => {
        state.translationLoading = false;
        state.translationError = action.payload as string;
      })
      // 리뷰 등록 액션 처리
      .addCase(createReview.pending, (state) => {
        state.reviewLoading = true;
        state.reviewError = null;
        state.reviewSuccess = false;
      })
      .addCase(createReview.fulfilled, (state) => {
        state.reviewLoading = false;
        state.reviewSuccess = true;
      })
      .addCase(createReview.rejected, (state, action) => {
        state.reviewLoading = false;
        state.reviewError = action.payload ? (action.payload as any).message : '리뷰 등록에 실패했습니다.';
      })
      // updateUserProfile 액션 처리
      .addCase(updateUserProfile.pending, (state) => {
        state.userInfoLoading = true;
        state.userInfoError = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.userInfoLoading = false;
        state.user = action.payload;
        state.userInfo = action.payload;
      })
      .addCase(updateUserProfile.rejected, (state, action: any) => {
        state.userInfoLoading = false;
        state.userInfoError = action.payload?.message || action.error.message || '사용자 정보 업데이트 실패';
      })
      // getUserById 액션 처리
      .addCase(getUserById.pending, (state) => {
        state.userInfoLoading = true;
        state.userInfoError = null;
      })
      .addCase(getUserById.fulfilled, (state, action) => {
        state.userInfoLoading = false;
        state.user = action.payload;
        state.userInfo = action.payload;
        // isLoggedIn과 isUserLoggedIn 업데이트
        state.isLoggedIn = true;
        state.isUserLoggedIn = true;
      })
      .addCase(getUserById.rejected, (state, action: any) => {
        state.userInfoLoading = false;
        state.userInfoError = action.payload?.message || action.error.message || '사용자 정보 조회 실패';
      });
  }
});

export const { 
  setWithcookieData, 
  clearWithcookieData, 
  setSavedProjects, 
  setCartItems,
  resetLoginState, 
  resetSignUpState, 
  resetProductsState,
  resetProductDetailState,
  setUser,
  clearUser,
  setAdminUser,
  clearAdminUser,
  logout,
  setSelectedAddress,
  setWithcookieDataLastLoaded,
  // 메뉴 관련 액션 추가
  setGlobalMenus,
  setMainMenus,
  clearMenus,
  // 결제 실패 관련 액션 추가
  setPaymentFailure,
  clearPaymentFailure,
  setCompanyIsUse,
  // 다국어 관련 액션 추가
  setCurrentLanguage,
  // 다국어 사전 직접 업데이트 액션 추가
  setTranslationDictionary,
  // 리뷰 상태 초기화 액션 추가
  resetReviewState,
  // company 정보 설정 액션 추가
  setCompany,
} = withcookieSlice.actions;

export const selectWithcookieData = (state: { withcookie: WithcookieState }) => state.withcookie.withcookieData;
export const selectSavedProjects = (state: { withcookie: WithcookieState }) => state.withcookie.savedProjects;

export const selectLoginState = (state: { withcookie: WithcookieState }) => ({
  loading: state.withcookie.loading,
  loginSuccess: state.withcookie.loginSuccess,
  loginError: state.withcookie.loginError,
  validationErrors: state.withcookie.validationErrors
});

export const selectSignUpState = (state: { withcookie: WithcookieState }) => ({
  loading: state.withcookie.loading,
  signUpSuccess: state.withcookie.signUpSuccess,
  signUpError: state.withcookie.signUpError,
  validationErrors: state.withcookie.validationErrors
});

export const selectProductsState = (state: { withcookie: WithcookieState }) => ({
  products: state.withcookie.products,
  loading: state.withcookie.productsLoading,
  error: state.withcookie.productsError
});

export const selectProductDetailState = (state: { withcookie: WithcookieState }) => ({
  productData: state.withcookie.productDetail,
  loading: state.withcookie.productDetailLoading,
  error: state.withcookie.productDetailError
});

export const selectAdminUser = (state: { withcookie: WithcookieState }) => state.withcookie.adminUser;
export const selectIsAdminLoggedIn = (state: { withcookie: WithcookieState }) => state.withcookie.isAdminLoggedIn;
export const selectUser = (state: { withcookie: WithcookieState }) => state.withcookie.user;
export const selectIsUserLoggedIn = (state: { withcookie: WithcookieState }) => state.withcookie.isUserLoggedIn;


export const selectUserInfo = (state: { withcookie: WithcookieState }) => {
  // API 응답 형식 체크
  const userInfo = state.withcookie.userInfo;
  
  
  // user나 administrator 객체가 있으면 로그인된 상태로 간주
  // userInfo 자체가 user 객체일 수 있음 (API 응답 그대로 저장된 경우)
  const user = userInfo?.user || userInfo?.administrator || userInfo;
  
  
  return user || null;
};

export const selectCartState = (state: { withcookie: WithcookieState }) => ({
  cartItems: state.withcookie.cartItems,
  loading: state.withcookie.cartLoading,
  error: state.withcookie.cartError
});

export const selectCompanyIsUse = (state: { withcookie: WithcookieState }) => state.withcookie.companyIsUse;

// 주문 내역 상태를 위한 셀렉터 추가
export const selectCheckoutsState = (state: { withcookie: WithcookieState }) => ({
  checkouts: state.withcookie.checkouts,
  loading: state.withcookie.checkoutsLoading,
  error: state.withcookie.checkoutsError,
  pagination: state.withcookie.checkoutsPagination
});

// 결제 확인 상태를 위한 셀렉터 추가
export const selectPaymentConfirmation = (state: { withcookie: WithcookieState }) => ({
  loading: state.withcookie.paymentConfirmation.loading,
  error: state.withcookie.paymentConfirmation.error,
  data: state.withcookie.paymentConfirmation.data,
  success: state.withcookie.paymentConfirmation.success
});

// 결제 실패 상태를 위한 셀렉터 추가
export const selectPaymentFailure = (state: { withcookie: WithcookieState }) => ({
  errorCode: state.withcookie.paymentFailure.errorCode,
  errorMessage: state.withcookie.paymentFailure.errorMessage,
  orderId: state.withcookie.paymentFailure.orderId
});

// localStorage에서 데이터를 가져오는 함수
const getStoredData = () => {
  try {
    const previewData = localStorage.getItem('previewData');
    const savedProjects = localStorage.getItem('savedProjects');
    return { previewData, savedProjects };
  } catch (error) {
    console.error('localStorage 데이터 가져오기 실패:', error);
    return { previewData: null, savedProjects: null };
  }
};

// withcookieData의 마지막 성공적인 로드 시간을 저장할 변수
let lastSuccessfulWithcookieDataLoad = 0;

// API 데이터를 가져오고 필요한 경우 previewData를 설정하는 thunk
export const fetchWithcookieData = () => async (dispatch: any, getState: any) => {
  // =========== API 호출 우선순위를 높이기 위한 설정 ===========
  
  // 브라우저 환경에서만 실행되는 로직
  if (typeof window !== 'undefined') {
    // 현재 진행 중인 모든 네트워크 요청을 일시 정지시키는 로직
    
    // 브라우저에서 지원하는 경우 navigator.connection.saveData를 임시로 true로 설정
    try {
      const nav = navigator as any;
      if (nav.connection) {
        const originalSaveData = nav.connection.saveData;
        nav.connection._originalSaveData = originalSaveData;
        
        // 임시로 저전력 모드 활성화 (다른 요청들의 우선순위를 줄이기 위함)
        Object.defineProperty(nav.connection, 'saveData', {
          get: function() { return true; },
          configurable: true
        });
        
        // 5초 후 원래 상태로 복원
        setTimeout(() => {
          if (nav.connection._originalSaveData !== undefined) {
            Object.defineProperty(nav.connection, 'saveData', {
              get: function() { return nav.connection._originalSaveData; },
              configurable: true
            });
          }
        }, 5000);
      }
    } catch (e) {
      console.warn('네트워크 설정 변경 실패:', e);
    }
  }
  
  // 현재 진행 중인 모든 API 호출의 AbortController 생성
  const existingControllers: AbortController[] = [];
  
  // 진행 중인 요청들을 모두 중단
  for (const key in pendingApiCalls) {
    if (key !== 'fetchWithcookieData') {
      try {
        // AbortController 생성 및 저장
        const controller = new AbortController();
        existingControllers.push(controller);
        
        // 가능하면 기존 요청 중단
        controller.abort();
      } catch (e) {
        console.warn('API 요청 중단 중 오류:', e);
      }
    }
  }
  

  // 현재 경로가 제한된 경로 목록에 있는지 확인
  const currentPath = window.location.pathname;
  const isRestrictedPath = RESTRICTED_PATHS.includes(currentPath);
  const isEditPath = currentPath === '/edit';
  
  // 제한된 경로에서는 API 호출을 최소화하고 리디렉션 방지
  if (isRestrictedPath) {
    // company.isUse = false로 상태 설정만 하고 API 호출은 하지 않음
    dispatch(setWithcookieData({ company: { isUse: false } }));
    return Promise.resolve({ company: { isUse: false } });
  }
      
  // 이미 진행 중인 API 호출이 있는지 확인
  const cacheKey = 'fetchWithcookieData';
  if (cacheKey in pendingApiCalls) {
    return pendingApiCalls[cacheKey];
  }
      
  // 현재 상태 확인 (이미 데이터가 있는 경우 불필요한 API 호출 방지)
  const currentState = getState();
  if (currentState.withcookie && currentState.withcookie.withcookieData) {
    // 마지막 로드 시간 확인 (10초에서 60초로 증가)
    const lastLoadTime = currentState.withcookie.withcookieDataLastLoaded || 0;
    const now = Date.now();
    
    // 마지막 성공적인 로드 이후 시간 확인
    const timeSinceLastSuccessfulLoad = now - lastSuccessfulWithcookieDataLoad;
    
    // 최소 지연 시간 계산 (정상 응답 시 60초, 비정상 응답 시 15초)
    const minDelay = lastSuccessfulWithcookieDataLoad > 0 ? 60000 : 15000;
    
    if (now - lastLoadTime < minDelay) { // 60초 이내 (이전 10초)
      return currentState.withcookie.withcookieData;
    }
    
    // 쿨다운 타임 계산 - 에러 발생 빈도에 따라 동적으로 조정
    if (timeSinceLastSuccessfulLoad < 300000 && lastSuccessfulWithcookieDataLoad === 0) { // 5분 이내에 성공한 로드가 없으면
      return currentState.withcookie.withcookieData;
    }
  }
  
  // 디바운싱 처리 (300ms에서 500ms로 증가)
  if (withcookieDataDebounceTimer) {
    clearTimeout(withcookieDataDebounceTimer);
  }
  
  // API 호출 Promise 생성 및 캐싱
  pendingApiCalls[cacheKey] = new Promise(async (resolve) => {
    // 디바운스 타이머 설정
    withcookieDataDebounceTimer = setTimeout(async () => {
      withcookieDataDebounceTimer = null;
      
      try {
        
        // 먼저 localStorage에서 데이터를 확인
        const storedData = getStoredData();
        if (storedData.previewData) {
          try {
            // 문자열을 객체로 파싱
            const parsedPreviewData = JSON.parse(storedData.previewData);
            
            
            // 컴포넌트와 이미지 데이터 검증
            if (parsedPreviewData.components && Array.isArray(parsedPreviewData.components)) {
              const imageComponents = parsedPreviewData.components.filter(
                (comp: any) => comp.type === 'IMAGE' || comp.type === 'image'
              );
              
              
            }
            
            await dispatch(setPreviewData(parsedPreviewData));
          } catch (error) {
            console.error('미리보기 데이터 파싱 오류:', error);
            // 파싱 오류 시에도 원본 문자열 저장 시도
            await dispatch(setPreviewData(storedData.previewData));
          }
        }
        if (storedData.savedProjects) {
          await dispatch(setSavedProjects(storedData.savedProjects));
        }

        // API 요청 - 타임아웃 설정 추가
        const apiBaseUrl = getApiBaseUrl();
        
        // 페치 요청에 타임아웃 추가
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10초 타임아웃
        
        try {
          const response = await fetch(`${apiBaseUrl}/api/check_current`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache',
              'Expires': '0'
            },
            credentials: 'include',
            signal: controller.signal
          });
          
          clearTimeout(timeoutId); // 타임아웃 제거
          
          // 304 응답 처리 추가 (Not Modified)
          if (response.status === 304) {
            
            // 성공적인 데이터 로드 시간 기록
            lastSuccessfulWithcookieDataLoad = Date.now();
            
            // 마지막 로드 시간 업데이트
            dispatch({ type: 'withcookie/setWithcookieDataLastLoaded', payload: Date.now() });
            
            // 현재 Redux 상태 가져오기
            const currentState = getState();
            const currentWithcookieData = currentState.withcookie.withcookieData;
            
            // 이미 Redux 상태에 데이터가 있다면 localStorage에 저장
            if (currentWithcookieData?.skin?.menuJsonNonUser?.preview_data) {
              const previewData = currentWithcookieData.skin.menuJsonNonUser.preview_data;
              
              try {
                // 데이터 타입 확인 후 저장
                const previewDataString = typeof previewData === 'string' 
                  ? previewData 
                  : JSON.stringify(previewData);
                
                // 항상 localStorage에 저장
                localStorage.setItem('previewData', previewDataString);
                localStorage.setItem('previewPath', '/');
                localStorage.setItem('previewTimestamp', new Date().toISOString());
                
              } catch (error) {
                console.error('304 응답 처리 중 localStorage 저장 오류:', error);
              }
            }
            
            // 저장된 프로젝트 데이터도 저장
            if (currentWithcookieData?.skin?.menuJsonNonUser?.saved_projects) {
              try {
                const savedProjects = currentWithcookieData.skin.menuJsonNonUser.saved_projects;
                localStorage.setItem('savedProjects', typeof savedProjects === 'string' 
                  ? savedProjects 
                  : JSON.stringify(savedProjects));
              } catch (error) {
                console.error('304 응답 처리 중 savedProjects 저장 오류:', error);
              }
            }
            
            resolve(currentWithcookieData);
            return;
          }
          
          if (!response.ok) {
            const errorData = await response.json();
            console.error('API 요청 실패:', errorData);
            
            if (response.status === 400) {
              dispatch(setWithcookieData({ skin: { menuJsonNonUser: {} } }));
              resolve({ skin: { menuJsonNonUser: {} } });
              return;
            }
            
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const data = await response.json();
          
          // 성공적인 데이터 로드 시간 기록
          lastSuccessfulWithcookieDataLoad = Date.now();
          
          // withcookieData 설정
          await dispatch(setWithcookieData(data));
          // 마지막 로드 시간 업데이트
          dispatch({ type: 'withcookie/setWithcookieDataLastLoaded', payload: Date.now() });

          // 받은 데이터를 전체 JSON으로 저장 (API 응답 데이터 전체)
          try {
            const fullDataString = JSON.stringify(data);
            localStorage.setItem('withcookieFullData', fullDataString);
          } catch (error) {
            console.error('전체 데이터 저장 오류:', error);
          }

          // menuJsonNonUser가 존재하는지 확인
          if (!data.skin?.menuJsonNonUser) {
            console.warn('menuJsonNonUser 데이터가 없습니다');
            
            // API 응답 전체 데이터를 직접 저장
            try {
              // API 응답 데이터에서 preview_data 구성
              const constructedPreviewData = {
                components: [],
                menus: [],
                layoutInfo: {
                  selectedLayout: 'layout01',
                  layoutProps: {
                    headerPosition: 'top',
                    showSidebar: false,
                    sidebarPosition: 'right'
                  }
                },
                timestamp: new Date().toISOString()
              };
              
              // 데이터를 JSON 문자열로 변환하여 저장
              const previewDataString = JSON.stringify(constructedPreviewData);
              
              // API 응답 전체 저장
              const fullDataString = JSON.stringify(data);
              
              localStorage.setItem('previewData', previewDataString);
              localStorage.setItem('withcookieFullData', fullDataString);
              localStorage.setItem('previewPath', '/');
              localStorage.setItem('previewTimestamp', new Date().toISOString());
              
              await dispatch(setPreviewData(constructedPreviewData));
            } catch (error) {
              console.error('데이터 저장 오류:', error);
            }
            
            // 이전에 저장된 데이터가 있다면 로컬 스토리지에 다시 저장 (기존 로직 유지)
            const storedData = getStoredData();
            if (storedData.previewData) {
              try {
                localStorage.setItem('previewData', storedData.previewData);
                localStorage.setItem('previewPath', '/');
                localStorage.setItem('previewTimestamp', new Date().toISOString());
              } catch (error) {
                console.error('기존 미리보기 데이터 저장 오류:', error);
              }
            }
            
            resolve(data);
            return;
          }
          
          // API에서 받은 새로운 데이터로 업데이트
          if (data.skin.menuJsonNonUser?.preview_data) {
            const newPreviewData = data.skin.menuJsonNonUser.preview_data;
            
            try {
              // preview_data가 문자열인지 확인하고 필요한 경우 파싱
              const isString = typeof newPreviewData === 'string';
              const parsedNewPreviewData = isString 
                ? JSON.parse(newPreviewData) 
                : newPreviewData;
                
              
              
              // Redux 스토어 업데이트
              await dispatch(setPreviewData(parsedNewPreviewData));
              
              // localStorage에 저장 - 항상 문자열로 변환
              const previewDataString = isString 
                ? newPreviewData 
                : JSON.stringify(parsedNewPreviewData);
              
              localStorage.setItem('previewData', previewDataString);
              localStorage.setItem('previewPath', '/');
              localStorage.setItem('previewTimestamp', new Date().toISOString());
              
            } catch (error) {
              console.error('미리보기 데이터 파싱 오류:', error);
              
              // 파싱 오류 시 원본 데이터 저장 시도
              try {
                // 원본이 문자열이면 그대로, 아니면 문자열로 변환
                const dataToStore = typeof newPreviewData === 'string' 
                  ? newPreviewData 
                  : JSON.stringify(newPreviewData);
                
                localStorage.setItem('previewData', dataToStore);
                localStorage.setItem('previewPath', '/');
                localStorage.setItem('previewTimestamp', new Date().toISOString());
                
                await dispatch(setPreviewData(
                  typeof newPreviewData === 'string' 
                    ? JSON.parse(newPreviewData) 
                    : newPreviewData
                ));
                
                
              } catch (storageError) {
                console.error('localStorage 저장 오류:', storageError);
              }
            }
          } else {
            
            // API 응답에서 직접 preview_data 구성
            try {
              // menuJsonNonUser에서 메뉴 데이터 가져오기
              const globalMenus = data.skin.menuJsonNonUser.global_menus 
                ? (typeof data.skin.menuJsonNonUser.global_menus === 'string' 
                  ? JSON.parse(data.skin.menuJsonNonUser.global_menus) 
                  : data.skin.menuJsonNonUser.global_menus) 
                : [];
                
              const mainMenus = data.skin.menuJsonNonUser.main_menus 
                ? (typeof data.skin.menuJsonNonUser.main_menus === 'string' 
                  ? JSON.parse(data.skin.menuJsonNonUser.main_menus) 
                  : data.skin.menuJsonNonUser.main_menus) 
                : [];
              
              // 기본 미리보기 데이터 구성
              const constructedPreviewData = {
                components: [],
                menus: [...globalMenus, ...mainMenus],
                layoutInfo: {
                  selectedLayout: 'layout01',
                  layoutProps: {
                    headerPosition: 'top',
                    showSidebar: false,
                    sidebarPosition: 'right'
                  }
                },
                timestamp: new Date().toISOString()
              };
              
              // 데이터를 JSON 문자열로 변환
              const previewDataString = JSON.stringify(constructedPreviewData);
              
              // localStorage에 저장
              localStorage.setItem('previewData', previewDataString);
              localStorage.setItem('previewPath', '/');
              localStorage.setItem('previewTimestamp', new Date().toISOString());
              
              // API 응답 전체도 저장
              const fullDataString = JSON.stringify(data);
              localStorage.setItem('withcookieFullData', fullDataString);
              
              
              // Redux 상태 업데이트
              await dispatch(setPreviewData(constructedPreviewData));
            } catch (error) {
              console.error('미리보기 데이터 구성 및 저장 오류:', error);
              
              // 기본 구조로 저장 시도
              try {
                const basicPreviewData = {
                  components: [],
                  menus: [],
                  layoutInfo: {
                    selectedLayout: 'layout01',
                    layoutProps: {
                      headerPosition: 'top',
                      showSidebar: false,
                      sidebarPosition: 'right'
                    }
                  },
                  timestamp: new Date().toISOString()
                };
                
                localStorage.setItem('previewData', JSON.stringify(basicPreviewData));
                localStorage.setItem('previewPath', '/');
                localStorage.setItem('previewTimestamp', new Date().toISOString());
                
                await dispatch(setPreviewData(basicPreviewData));
              } catch (basicError) {
                console.error('기본 구조 저장 오류:', basicError);
              }
            }
            
            // 이전에 저장된 데이터가 있다면 백업용으로 확인 (로그만 남김)
            const storedData = getStoredData();
            if (storedData.previewData) {
              
            }
          }

          if (data.skin.menuJsonNonUser?.saved_projects) {
            const newSavedProjects = data.skin.menuJsonNonUser.saved_projects;
            await dispatch(setSavedProjects(newSavedProjects));
            
            try {
              localStorage.setItem('savedProjects', newSavedProjects);
            } catch (error) {
              console.error('저장된 프로젝트 데이터 저장 오류:', error);
            }
          }

          resolve(data);

          if (data.skin?.company?.isUse === false) {
            const styles = [
              'color: #ffffff',
              'background-color:rgb(234, 51, 51)',
              'font-size: 18px',
              'font-weight: bold',
              'padding: 10px 15px',
              'border-radius: 5px',
              'text-shadow: 1px 1px 2px #000',
              'margin: 20px 0'
            ].join(';');
          

            // 회사 사용 상태를 false로 직접 설정
            dispatch(setCompanyIsUse(false));
            
          }

          // 다국어 사전 데이터 로드
          try {
            dispatch(fetchTranslationDictionary());
          } catch (error) {
            console.error('다국어 사전 로드 중 오류:', error);
          }

          // company 정보 설정 추가
          if (data.skin?.company) {
            console.log("API에서 받은 company 객체:", data.skin.company);
            dispatch(setCompany(data.skin.company));
          }

        } catch (fetchError: any) {
          clearTimeout(timeoutId); // 타임아웃 제거
          if (fetchError.name === 'AbortError') {
            console.error('[API 호출 타임아웃] check_current 요청이 10초 내에 완료되지 않아 중단되었습니다');
          } else {
            console.error('check_current API 호출 중 오류:', fetchError);
          }
          throw fetchError;
        }
      } catch (error) {
        console.error('Withcookie 데이터 가져오기 실패:', error);
        resolve(null);
      } finally {
        // API 호출이 완료되면 캐시에서 제거 (짧은 지연 후)
        setTimeout(() => {
          delete pendingApiCalls[cacheKey];
        }, 500); // 300ms에서 500ms로 증가
      }
    }, 500); // 디바운스 시간을 300ms에서 500ms로 증가
  });
  
  return pendingApiCalls[cacheKey];
};

// 셀렉터 추가
export const selectAddressResults = (state: { withcookie: WithcookieState }) => state.withcookie.addressResults;
export const selectAddressLoading = (state: { withcookie: WithcookieState }) => state.withcookie.addressLoading;
export const selectAddressError = (state: { withcookie: WithcookieState }) => state.withcookie.addressError;
export const selectSelectedAddress = (state: { withcookie: WithcookieState }) => state.withcookie.selectedAddress;

// 추가 셀렉터 내보내기
export const selectGlobalMenus = (state: { withcookie: WithcookieState }) => state.withcookie.globalMenus;
export const selectMainMenus = (state: { withcookie: WithcookieState }) => state.withcookie.mainMenus;
export const selectMenuLoading = (state: { withcookie: WithcookieState }) => state.withcookie.menuLoading;
export const selectMenuError = (state: { withcookie: WithcookieState }) => state.withcookie.menuError;

// 팝업 관련 셀렉터 추가
export const selectPopups = (state: { withcookie: WithcookieState }) => state.withcookie.popups;
export const selectPopupsLoading = (state: { withcookie: WithcookieState }) => state.withcookie.popupsLoading;
export const selectPopupsError = (state: { withcookie: WithcookieState }) => state.withcookie.popupsError;

// 게시판 상태 선택자 추가
export const selectBoards = (state: { withcookie: WithcookieState }) => state.withcookie.boards;
export const selectBoardsLoading = (state: { withcookie: WithcookieState }) => state.withcookie.boardsLoading;
export const selectBoardsError = (state: { withcookie: WithcookieState }) => state.withcookie.boardsError;

export const selectPosts = (state: { withcookie: WithcookieState }) => state.withcookie.posts;
export const selectPostsLoading = (state: { withcookie: WithcookieState }) => state.withcookie.postsLoading;
export const selectPostsError = (state: { withcookie: WithcookieState }) => state.withcookie.postsError;
export const selectPostsPagination = (state: { withcookie: WithcookieState }) => state.withcookie.postsPagination;

export const selectPostDetail = (state: { withcookie: WithcookieState }) => state.withcookie.postDetail;
export const selectPostDetailLoading = (state: { withcookie: WithcookieState }) => state.withcookie.postDetailLoading;
export const selectPostDetailError = (state: { withcookie: WithcookieState }) => state.withcookie.postDetailError;

// 다국어 사전 관련 셀렉터 추가
export const selectTranslationDictionary = (state: { withcookie: WithcookieState }) => state.withcookie.translationDictionary;
export const selectTranslationLoading = (state: { withcookie: WithcookieState }) => state.withcookie.translationLoading;
export const selectTranslationError = (state: { withcookie: WithcookieState }) => state.withcookie.translationError;
export const selectCurrentLanguage = (state: { withcookie: WithcookieState }) => state.withcookie.currentLanguage;

// 번역 함수 - 키와 기본 언어 코드를 받아 번역된 텍스트 반환
export const getTranslation = (state: { withcookie: WithcookieState }, key: string, defaultLang: string = 'KR') => {
  const dictionary = state.withcookie.translationDictionary;
  const currentLang = state.withcookie.currentLanguage.toUpperCase(); // 언어 코드는 대문자로 통일
  
  // 사전에 키가 없으면 원본 키 반환
  if (!dictionary[key]) {
    return key;
  }
  
  // 현재 언어로 번역이 있으면 반환, 없으면 기본 언어로 반환
  return dictionary[key][currentLang] || dictionary[key][defaultLang] || key;
};

export const selectReviewState = (state: { withcookie: WithcookieState }) => ({
  loading: state.withcookie.reviewLoading,
  error: state.withcookie.reviewError,
  success: state.withcookie.reviewSuccess
});

export const selectCompanyState = (state: { withcookie: WithcookieState }) => state.withcookie.company;

export default withcookieSlice.reducer; 